import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store/store';
import { logoutUser, clearUser, setUser, setSession } from '../features/auth/authSlice';
import { supabase } from '../utils/supabase';

export const useAuth = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    // Check initial session
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        dispatch(setUser(session.user));
        dispatch(setSession({
          access_token: session.access_token,
          refresh_token: session.refresh_token,
        }));
      } else {
        dispatch(clearUser());
      }
    };
    checkSession();

    // Setup auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session) {
          dispatch(setUser(session.user));
          dispatch(setSession({
            access_token: session.access_token,
            refresh_token: session.refresh_token,
          }));
        } else {
          dispatch(clearUser());
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [dispatch]);

  const logout = useCallback(async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      dispatch(clearUser());
      navigate('/', { replace: true });
    }
  }, [dispatch, navigate]);

  return {
    isAuthenticated: !!auth.user,
    user: auth.user,
    loading: auth.loading,
    error: auth.error,
    logout,
  };
};